import apiClient from "@/api/apiClient";
import type {
  CreateMaintenanceFromChecklistTemplateProps,
  CreateMaintenanceFromDevicesProps,
  CreateMaintenanceFromMaintenanceProps,
  CreateMaintenanceFromMaintenanceStrategyProps,
  FinishMaintenanceProps,
  NotifyMaintenanceAssigneeProps,
  NotifyMaintenanceCreationProps,
  RawMaintenance,
  SkipMaintenanceProps,
  StartMaintenanceProps,
} from "@/api/resources/maintenances/types";
import { MaintenanceStatus, type ArgsExportMaintenanceForCsv } from "@/supabase.types";
import {
  createPaginatedSupabaseQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";
import { getDateEndOfDay, getDateStartOfDay } from "@/utils/dates";
import { type QueryFilter } from "@/utils/filter";
import { type QueryPaginationFilters } from "@/utils/pagination";

/** ! MAINTENANCE FILTERING BY VIEW:
 * HISTORY:
 * - all maintenances COMPLETED
 * - all maintenances SKIPPED
 * OPEN:
 * - all maintenances OVERDUE (planned_at OR scheduled_at <= startOfToday)
 * - all maintenances TODAY (planned_at OR scheduled_at BETWEEN startOfToday <-> endOfToday)
 * - all maintenances STARTED (started_at <= endOfToday) (NOTE: started_at can't be higher than NOW)
 * UPCOMING:
 * - all maintenances TODAY (planned_at OR scheduled_at BETWEEN startOfToday <-> endOfToday)
 * - all maintenances STARTED (started_at >= startOfToday) (NOTE: started_at can't be higher than NOW)
 * - all maintenances SCHEDULED (planned_at OR scheduled_at >= startOfToday)
 * - all virtual maintenances (effectively scheduled_at >= startOfToday)
 *
 * OVERLAP OPEN + UPCOMING:
 * - OPEN (all maintenances STARTED) + (all maintenances TODAY)
 * - UPCOMING (all maintenances STARTED) + (all maintenances TODAY)
 */

const table = "maintenances";
const query = `
                *,
                public_id,
                planned_date,
                parent_maintenance(*),
                child_maintenances:maintenances(id),
                child_cases:cases!cases_parent_maintenance_id_fkey(id),
                assignee:user_profiles!maintenances_assignee_id_fkey(*),
                maintenances_to_devices(device_id, device:devices(id, device_tags(id))),
                maintenance_items(status),
                maintenance_strategy:maintenance_strategies(
                  title,
                  maintenance_strategies_to_devices(device_id)
                ),
                maintenance_tags(*),
                search_s:maintenance_strategies(title),
                search_t:maintenance_tags(*)
                `;
export const apiConfigMaintenances: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["title", "notes", "skip_reason", "public_id", "search_t.name", "search_s.title"],
  sortables: [
    "title",
    "status",
    "updated_at",
    "created_at",
    "resolved_at",
    "scheduled_at",
    "planned_at",
    "planned_date",
    "public_id",
  ],
};

export const getMaintenance = spawnGetApiFunction({ table, query });
export const createMaintenance = spawnCreateApiFunction({ table, query });
export const updateMaintenance = spawnUpdateApiFunction({ table, query });
export const deleteMaintenance = spawnDeleteApiFunction({ table });

export const getChildMaintenances = async (maintenance_id: string) => {
  const { data, error } = await apiClient.from(table).select(query).eq("parent_maintenance_id", maintenance_id);
  if (error) throw error;
  return data;
};

export const getMaintenancesUpcomingMaterialized = async (customFilters?: QueryFilter[]) => {
  const q = apiClient
    .from(table)
    .select(query)
    .or(
      `and(status.eq.${MaintenanceStatus.scheduled},planned_date.gt.${getDateStartOfDay()}),and(status.eq.${MaintenanceStatus.started},started_at.gte.${getDateStartOfDay()})`,
    );

  if (customFilters && customFilters.length > 0) {
    for (const filter of customFilters) {
      q.filter(...filter);
    }
  }

  const { data, error } = await q;
  if (error) throw error;
  return data;
};

export async function getPaginatedMaintenancesOpen({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigMaintenances.searchables,
      sortables: apiConfigMaintenances.sortables,
      filters: [["planned_date", "lt", getDateEndOfDay()]],
      orFilters: [`status.eq.${MaintenanceStatus.scheduled}, status.eq.${MaintenanceStatus.started}`],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedMaintenancesHistory({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigMaintenances.searchables,
      sortables: apiConfigMaintenances.sortables,
      orFilters: [`status.eq.${MaintenanceStatus.skipped}, status.eq.${MaintenanceStatus.finished}`],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export const createMaintenanceFromMaintenance = async ({
  maintenance_id: _maintenance_id,
  devices: _device_ids,
  deployment_id: _deployment_id,
  assignee_id: _assignee_id,
  title: _title,
}: CreateMaintenanceFromMaintenanceProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("maintenance_from_maintenance", {
    _maintenance_id,
    _device_ids,
    _assignee_id,
    _title,
    _deployment_id,
  });
  if (error) throw error;
  return data;
};

export const createMaintenanceFromDevices = async ({
  devices: _device_ids,
  deployment_id: _deployment_id,
  assignee_id: _assignee_id,
  title: _title,
}: CreateMaintenanceFromDevicesProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("maintenance_from_devices", {
    _device_ids,
    _assignee_id,
    _title,
    _deployment_id,
  });
  if (error) throw error;
  return data;
};

export const createMaintenanceFromChecklistTemplate = async ({
  checklist_template_id: _checklist_template_id,
  devices: _device_ids,
  deployment_id: _deployment_id,
  assignee_id: _assignee_id,
  title: _title,
}: CreateMaintenanceFromChecklistTemplateProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("maintenance_from_checklist_template", {
    _checklist_template_id,
    _device_ids,
    _assignee_id,
    _title,
    _deployment_id,
  });

  if (error) throw error;
  return data;
};

export const createMaintenanceFromMaintenanceStrategy = async ({
  maintenance_strategy_id: _maintenance_strategy_id,
  scheduled_at: _scheduled_at,
  assignee_id: _assignee_id,
  title: _title,
}: CreateMaintenanceFromMaintenanceStrategyProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("maintenance_from_maintenance_strategy", {
    _maintenance_strategy_id,
    _scheduled_at,
    _assignee_id,
    _title,
  });
  if (error) throw error;
  return data;
};

export const startMaintenance = async ({
  maintenance_id: _maintenance_id,
  user_id: _user_id,
}: StartMaintenanceProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("start_maintenance", {
    _maintenance_id,
    _user_id,
  });
  if (error) throw error;
  return data;
};

export const skipMaintenance = async ({
  maintenance_id: _maintenance_id,
  skip_reason: _skip_reason,
  user_id: _user_id,
}: SkipMaintenanceProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("skip_maintenance", {
    _maintenance_id,
    _skip_reason,
    _user_id,
  });
  if (error) throw error;
  return data;
};

export const finishMaintenance = async ({
  maintenance_id: _maintenance_id,
  user_id: _user_id,
  skip_remaining_items: _skip_remaining_items,
}: FinishMaintenanceProps): Promise<RawMaintenance | null> => {
  const { error, data } = await apiClient.rpc("finish_maintenance", {
    _maintenance_id,
    _user_id,
    _skip_remaining_items,
  });
  if (error) throw error;
  return data;
};

export const notifyMaintenanceAssignee = async ({
  maintenanceId,
  previousUserId,
  whitelabelBrandKey,
  appBaseUrl,
}: NotifyMaintenanceAssigneeProps) => {
  const response = await apiClient.functions.invoke("notify-maintenance-assignee", {
    body: {
      whitelabelBrandKey,
      appBaseUrl,
      maintenanceId,
      previousUserId,
    },
  });
  if (response.error) throw response.error;
};

export const notifyMaintenanceCreation = async ({
  maintenanceId,
  email,
  locale,
  whitelabelBrandKey,
  appBaseUrl,
}: NotifyMaintenanceCreationProps) => {
  const response = await apiClient.functions.invoke("notify-maintenance-creation", {
    body: {
      whitelabelBrandKey,
      appBaseUrl,
      maintenanceId,
      email,
      locale,
    },
  });
  if (response.error) throw response.error;
};

export const exportMaintenancesCsv = async (args: ArgsExportMaintenanceForCsv) => {
  const { error, ...rest } = await apiClient.rpc("export_maintenances_for_csv", args).csv();
  if (error) throw error;

  return { error, ...rest };
};
